/* eslint-disable no-use-before-define */
import React, {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState
} from 'react'
import { Box, Checkbox, InputBase, Skeleton, SxProps } from '@mui/material'
import { GridRenderEditCellParams } from '@mui/x-data-grid-pro'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'
import { DayDataTypes } from '../../../../../../api/models/dataList.model'
import { RestrictionTypeListType } from '../../../../../../api/models/restrictionTypeList.model'
import { RestrictionEditableByChannelList } from 'src/api'
import RatesGridTypeAvailablesCell from './components/RatesGridTypeAvailablesCell'
import {
  CapacityRow,
  ChannelRow,
  RateRow,
  RatesDataGridRow,
  RowType
} from '../../contexts/ratesDataGrid/RatesDataGridContext.types'
import { useRatesDataGridContext } from '../../contexts/ratesDataGrid/RatesDataGridContext'
import { dateUtils } from 'src/utils/date.util'
interface Props {
  params: GridRenderEditCellParams
  gridApiRef: React.MutableRefObject<GridApiPro>
  restrictionEditableByChannelList: RestrictionEditableByChannelList[]
  openLegendModal: () => void
  columnIndex: number
  dayDate: Date
}

const DATA_TYPE_MAP: {
  [key in keyof Required<DayDataTypes>]: RestrictionTypeListType
} = {
  price: RestrictionTypeListType.Number,
  closed: RestrictionTypeListType.Boolean,
  maxAnticipation: RestrictionTypeListType.Time,
  closedOnArrival: RestrictionTypeListType.Boolean,
  closedOnDeparture: RestrictionTypeListType.Boolean,
  exactStay: RestrictionTypeListType.Number,
  exactStayArrival: RestrictionTypeListType.Number,
  maximumStay: RestrictionTypeListType.Number,
  maximumStayArrival: RestrictionTypeListType.Number,
  minimumStay: RestrictionTypeListType.Number,
  minimumStayArrival: RestrictionTypeListType.Number,
  release: RestrictionTypeListType.Time,
  roomToSell: RestrictionTypeListType.Number,
  availables: RestrictionTypeListType.Number
}

const RatesGridEditCellRender: ReactFC<Props> = ({
  params,
  openLegendModal,
  columnIndex,
  dayDate
}) => {
  const { setEditDateData, editedDaysList } = useRatesDataGridContext()
  const row = params.row as RatesDataGridRow
  const editedValue = useMemo(
    () =>
      editedDaysList[row.id]?.[dateUtils.formatDateToString(dayDate)]?.[
        (row as RateRow).dayDataType
      ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dayDate, editedDaysList, (row as RateRow).dayDataType, row.id]
  )
  const [inputVal, setInputVal] = useState<
    string | number | boolean | undefined
  >(
    editedValue ??
      ('data' in row &&
        'dayDataType' in row &&
        row.data.dayDataList[columnIndex]?.[row.dayDataType])
  )

  useLayoutEffect(() => {
    if ('dayDataType' in row && 'data' in row) {
      const value =
        editedValue ?? row.data.dayDataList[columnIndex]?.[row.dayDataType]

      setInputVal(value)

      if (!editedValue) {
        params.api
          .getCellElement(row.id, params.field)
          ?.classList.remove('editedCell')
      }
    }
  }, [
    columnIndex,
    dayDate,
    editedDaysList,
    editedValue,
    params.api,
    params.field,
    row
  ])

  const handleInputChange = useCallback(
    (value: number | string | boolean) => {
      const rateRow = row as RateRow
      setEditDateData({
        date: dayDate,
        dayData: {
          [rateRow.dayDataType]: value
        },
        row: rateRow
      })
      setInputVal(value)
      params.api
        .getCellElement(row.id, params.field)
        ?.classList.add('editedCell')
    },
    [dayDate, params.api, params.field, row, setEditDateData]
  )

  if (row.type.includes('Skeleton')) {
    return <Skeleton variant='rectangular' width='100%' height='60%' />
  }

  if (row.type === RowType.ROOM_TYPE) {
    return (
      <RatesGridTypeAvailablesCell
        availabilityData={row.data.availabilityDataList[columnIndex]}
        onClick={() => openLegendModal()}
      />
    )
  }

  if (
    row.type !== RowType.CAPACITY &&
    row.type !== RowType.RATE &&
    row.type !== RowType.CHANNEL
  )
    return null

  const rateRow = row as RateRow | CapacityRow | ChannelRow
  const type = DATA_TYPE_MAP[rateRow.dayDataType] || DATA_TYPE_MAP.price

  const editable = row.editable || row.dayDataType !== 'price'

  const isFatherRow = !row.inherited && !row.fatherId

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMemo(
    () => (
      <Box
        sx={{
          '& *': {
            ...(!editable &&
              (row.type === RowType.CHANNEL ||
                row.type === RowType.RATE ||
                row.type === RowType.CAPACITY) && {
                cursor: 'not-allowed !important'
              })
          }
        }}
      >
        {
          {
            [RestrictionTypeListType.Boolean]: (
              <EditCheckbox
                checked={!!inputVal as boolean}
                disabled={!editable}
                onChange={handleInputChange}
              />
            ),
            [RestrictionTypeListType.Time]: (
              <EditInput
                value={inputVal?.toString() ?? ''}
                disabled={!editable}
                onChange={handleInputChange}
                onlyInt
                {...(isFatherRow && {
                  sx: {
                    fontWeight: 600
                  }
                })}
              />
            ),
            [RestrictionTypeListType.Number]: (
              <EditInput
                value={inputVal?.toString() ?? ''}
                disabled={!editable}
                onChange={handleInputChange}
                {...(isFatherRow && {
                  sx: {
                    fontWeight: 600
                  }
                })}
              />
            )
          }[type]
        }
      </Box>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row.type, inputVal, handleInputChange, type]
  )
}

const EditCheckbox = ({
  onChange,
  disabled,
  checked
}: {
  onChange: (value: boolean) => void
  disabled: boolean
  checked: boolean
}) => {
  return (
    <Checkbox
      disabled={disabled}
      checked={checked}
      onChange={(e) => {
        const newChecked = e.target.checked
        onChange(newChecked)
      }}
    />
  )
}

const EditInput = ({
  disabled,
  onChange,
  onlyInt,
  sx,
  value
}: {
  disabled: boolean
  onChange: (value: number | string) => void
  onlyInt?: boolean
  sx?: SxProps
  value: string
}) => {
  // useEffect(() => {
  //   setInputVal(defaultValue)
  // }, [defaultValue, ratesStateData])

  return (
    <InputBase
      sx={{
        '& > *': {
          textAlign: 'center'
        },
        ...sx
      }}
      disabled={disabled}
      onChange={(e) => {
        const newVal = onlyInt
          ? e.target.value.replace(/[^\d]/gi, '')
          : e.target.value.replace(/[^\d.]/gi, '')
        onChange(newVal)
      }}
      value={value}
    />
  )
}

// const EditTime = () => {
//   const [timeVal, setTimeVal] = useState<Date | null>(null)
//   return (
//     <LocalizationProvider
//       dateAdapter={AdapterMoment}
//       locale='es'
//     >
//       <MobileTimePicker
//         onChange={(newValue) => {
//           setTimeVal(newValue)
//         }}
//         value={timeVal}
//         renderInput={(params) => (
//           <InputBase
//             {...(params as any)}
//             sx={{
//               '& > *': {
//                 textAlign: 'center'
//               }
//             }}
//           />
//         )}
//       />
//     </LocalizationProvider>
//   )
// }

export default memo(RatesGridEditCellRender)
